import { PARTICIPANT_ACTOR_KEY } from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import {
  IReportingQuery,
  IReportingQueryDrilldown
} from '@dadohr/shared/domainTypes/reports';
import {
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from '@material-ui/core';
import {
  ArrowDown01,
  ArrowDown10,
  ArrowDownAZ,
  ArrowDownZA
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AutocompleteSingle } from '../../../../components/Autocomplete';
import { Loader } from '../../../../components/Loader';
import { TableSearch } from '../../../../components/Table';
import { TeaserBoxFreeTierForExperiences } from '../../../../components/TeaserBox';
import { useErrorLogger } from '../../../../hooks/useErrorLogger';
import { useTrackDuration } from '../../../../hooks/useTrackDuration';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { PageBody } from '../../../../layout/PageBody';
import { PageToolbar, PageToolbarTitle } from '../../../../layout/PageToolbar';
import { Section } from '../../../../layout/Section';
import {
  useRoutes,
  useStringQueryParam,
  useTypedStringQueryParam
} from '../../../../routes';
import {
  useCurrentUser,
  useIsCurrentUserDadoEmployee
} from '../../../../services/currentUser';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { EmptyStateNoReportableTemplates } from '../../components/EmptyState';
import { ReportCardOuterContainer } from '../../components/ReportCard';
import { ReportFilters } from '../../components/ReportFilters';
import { useReportsSideNav } from '../../layout';
import {
  parseReportingQueryParams,
  useDefaultReportingQueryParams
} from '../../services/data';
import { BodyByActor } from './components/BodyByActor';
import { BodyByInstance } from './components/BodyByInstance';
import { BodyByParticipant } from './components/BodyByParticipant';
import { TrackingFns } from './components/common';
import { TypeHeading, TypeSwitchForTemplate } from './components/TypeSwitch';
import { ReportDrilldownSortKeys } from './types';

const SortSettings = ({
  value,
  onChange
}: {
  value: ReportDrilldownSortKeys;
  onChange: (nextValue: ReportDrilldownSortKeys) => void;
}) => {
  // Lucide doesn't have these icons?
  const { t } = useUnsafeTranslation();
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const options = useMemo(
    () => [
      {
        key: ReportDrilldownSortKeys.COMPLETION_RATE_ASC,
        icon: ArrowDown01,
        text: t('Completion rate (low to high)')
      },
      {
        key: ReportDrilldownSortKeys.COMPLETION_RATE_DESC,
        icon: ArrowDown10,
        text: t('Completion rate (high to low)')
      },
      {
        key: ReportDrilldownSortKeys.NAME_ASC,
        icon: ArrowDownAZ,
        text: t('Name (A-Z)')
      },
      {
        key: ReportDrilldownSortKeys.NAME_DESC,
        icon: ArrowDownZA,
        text: t('Name (Z-A)')
      }
    ],
    [t]
  );

  const selected = useMemo(() => options.find((o) => o.key === value) || null, [
    options,
    value
  ]);
  if (!selected) {
    // cannot happen
    return null;
  }
  const close = () => setOpen(false);
  return (
    <>
      <IconButton ref={ref} onClick={() => setOpen((x) => !x)}>
        <Tooltip title={selected.text}>
          <selected.icon size={24} />
        </Tooltip>
      </IconButton>
      <Menu
        keepMounted
        anchorEl={ref.current}
        open={open}
        onClose={() => close()}
      >
        <ListSubheader>{t('Sort by')}</ListSubheader>
        {options.map((o) => (
          <MenuItem
            selected={o === selected}
            key={o.key}
            onClick={() => {
              onChange(o.key);
              close();
            }}
          >
            <FlexContainer>
              <o.icon size={20} color="currentColor" />
              <div>{o.text}</div>
            </FlexContainer>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const Body = ({
  q,
  setQ,
  defaultQ,
  search,
  setSearch,
  sort,
  setSort
}: {
  q: IReportingQueryDrilldown;
  setQ: (nextQ: IReportingQueryDrilldown | null) => void;
  defaultQ: IReportingQuery | void;
  search: string;
  setSearch: (nextSearch: string) => void;
  sort: ReportDrilldownSortKeys;
  setSort: (nextSort: ReportDrilldownSortKeys) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const type = q.otherData?.type || 'everyone';
  const { trackEvent } = useProductAnalytics();
  const { startTimer, getDuration } = useTrackDuration();

  const tplId = q.tplIds[0] || '';
  useEffect(() => {
    startTimer();
    if (tplId) {
      trackEvent({
        eventType: PAEventType.EXPERIENCE_TPL,
        'Experience Template ID': tplId,
        name: 'Drilldown Report Page Loaded',
        Explanation: 'Admin user loaded the Drilldown report page',
        'Task Assignees': type
      });
    }

    return () => {
      if (tplId) {
        trackEvent({
          eventType: PAEventType.EXPERIENCE_TPL,
          name: 'Drilldown Report Page Exited',
          'Experience Template ID': tplId,
          Explanation: 'Admin user exited the Drilldown report page',
          'Time On Page': Math.round(getDuration() / 1000),
          'Task Assignees': type
        });
      }
    };
  }, [trackEvent, tplId, getDuration, startTimer, type]);

  const trackSearch = (nextSearch: string) => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_TPL,
      name: 'Drilldown Report Search Applied',
      Explanation: 'Admin user put a term into the search box',
      'Experience Template ID': tplId,
      'Task Assignees': type,
      Term: nextSearch
    });
  };

  const trackSort = (nextSort: ReportDrilldownSortKeys) => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_TPL,
      name: 'Drilldown Report Sort Order Changed',
      Explanation:
        'Admin user changed the sort order of the rows in the Drilldown report',
      'Experience Template ID': tplId,
      'Task Assignees': type,
      'Sort Type': nextSort.startsWith('name') ? 'Name' : 'Completion Rate',
      'Sort Direction': nextSort.endsWith('asc') ? 'ASC' : 'DESC'
    });
  };

  const trackingFns = useMemo<TrackingFns>(() => {
    return {
      trackBlockHover: (tsk, status, blockType) => {
        trackEvent({
          eventType: PAEventType.EXPERIENCE_TPL,
          name: 'Drilldown Report Task Block Hovered',
          Explanation:
            'Admin user hovered over a block in the Drilldown report',
          'Task Assignees': type,
          'Task Name': tsk.title,
          'Task ID': tsk.id,
          'Task Status': status,
          'Block Type': blockType === 'status' ? 'Status' : 'Aggregated Status',
          'Experience Template ID': tsk.tplId
        });
      },
      trackBlockClick: (tsk, status, blockType) => {
        trackEvent({
          eventType: PAEventType.EXPERIENCE_TPL,
          name: 'Drilldown Report Task Block Clicked',
          Explanation: 'Admin user clicked on a block in the Drilldown report',
          'Task Assignees': type,
          'Task Name': tsk.title,
          'Task ID': tsk.id,
          'Task Status': status,
          'Block Type': blockType === 'status' ? 'Status' : 'Aggregated Status',
          'Experience Template ID': tsk.tplId
        });
      },
      trackRowExpanded: (tplId, rowName) => {
        trackEvent({
          eventType: PAEventType.EXPERIENCE_TPL,
          name: 'Drilldown Report Row Expanded',
          Explanation: 'Admin user expanded a row in the Drilldown report',
          'Task Assignees': type,
          'Row Expanded': rowName,
          'Experience Template ID': tplId
        });
      },
      openInstanceDialog: (instanceId) => {
        trackEvent({
          eventType: PAEventType.EXPERIENCE_INSTANCE,
          name: 'Admin Instance View Loaded',
          'Instance ID': instanceId,
          Trigger: 'Drilldown Report',
          Explanation: 'Admin user views the details of a single instance'
        });
      },
      openExecutionDialog: (tsk) => {
        trackEvent({
          eventType: PAEventType.EXPERIENCE_TPL,
          name: 'Execution Report Opened',
          Explanation:
            'Admin user opened the report showing status of a timeline item across multiple instances, aka the People Tab',
          'Experience Template ID': tsk.tplId,
          'Timeline Item ID': tsk.id,
          'Timeline Item Type': 'TASK',
          'Timeline Item Title': tsk.title,
          Trigger: 'Drilldown Report'
        });
      }
    };
  }, [trackEvent, type]);
  return (
    <>
      <Section spacing={4}>
        <ReportFilters<{
          type: 'everyone' | string;
          scope: 'ACTIVE' | 'ACTIVE_AND_ARCHIVED';
        }>
          value={q}
          onChange={setQ}
          initialValue={defaultQ}
          additionalElements={({
            state,
            setState,
            selectedTpl,
            isInSideBar
          }) => {
            const type = state.otherData?.type || 'everyone';
            const scope = state.otherData?.scope || 'ACTIVE';
            const setType = (nextType: string) =>
              setState((x) => ({
                ...x,
                otherData: {
                  ...x.otherData,
                  type: nextType,
                  scope
                }
              }));
            const setScope = (nextScope: 'ACTIVE' | 'ACTIVE_AND_ARCHIVED') => {
              setState((x) => ({
                ...x,
                otherData: {
                  ...x.otherData,
                  type,
                  scope: nextScope
                }
              }));
            };
            const scopeOptions = [
              {
                label: t('Active experiences only'),
                value: 'ACTIVE' as const
              },
              {
                label: t('Active and archived'),
                value: 'ACTIVE_AND_ARCHIVED' as const
              }
            ];
            return (
              <>
                <TypeSwitchForTemplate
                  tpl={selectedTpl}
                  type={type}
                  setType={setType}
                  maxWidth={isInSideBar ? undefined : 250}
                />
                <AutocompleteSingle
                  label={t('Experience status')}
                  value={
                    scopeOptions.find((x) => x.value === scope) ||
                    scopeOptions[0]
                  }
                  onChange={(nextScope) =>
                    nextScope && setScope(nextScope.value)
                  }
                  disabledClearable
                  options={scopeOptions}
                  maxWidth={isInSideBar ? undefined : 250}
                  getOptionLabel={(o) => o.label}
                  fullWidth
                />
              </>
            );
          }}
          additionalTrackingProps={(ev) => ({
            ...ev,
            Report: 'Drilldown',
            'Task Assignees': type
          })}
          provideDrawer
        />
      </Section>

      <Section>
        <ReportCardOuterContainer>
          <FlexContainer
            fullWidth
            marginBottom={3}
            justifyContent="space-between"
          >
            <TypeHeading type={type} tplIds={q.tplIds} />
            <FlexContainer spacing={2}>
              <SortSettings
                value={sort}
                onChange={(nextSort) => {
                  trackSort(nextSort);
                  setSort(nextSort);
                }}
              />
              <TableSearch
                term={search}
                onChange={(nextSearch) => {
                  if (nextSearch) {
                    trackSearch(nextSearch);
                  }
                  setSearch(nextSearch);
                }}
                placeholder={t('Search by name')}
              />
            </FlexContainer>
          </FlexContainer>
          {type === 'everyone' ? (
            <BodyByInstance
              q={q}
              search={search}
              sort={sort}
              trackingFns={trackingFns}
            />
          ) : type === PARTICIPANT_ACTOR_KEY ? (
            <BodyByParticipant
              q={q}
              search={search}
              sort={sort}
              trackingFns={trackingFns}
            />
          ) : (
            <BodyByActor
              q={q}
              search={search}
              sort={sort}
              trackingFns={trackingFns}
            />
          )}
        </ReportCardOuterContainer>
      </Section>
    </>
  );
};

export const PageReportsDrilldown = () => {
  const { t } = useUnsafeTranslation();
  const sideNav = useReportsSideNav();
  const { ROUTES, goTo } = useRoutes();
  const currentUser = useCurrentUser();

  const [queryParam] = useStringQueryParam('q', '');
  const q = parseReportingQueryParams(queryParam);
  const setQ = (nextQ: IReportingQueryDrilldown | null) =>
    goTo(ROUTES.reports.drilldown.url(nextQ));
  const [defaultQ, loadingQ, errorDefaultQ] = useDefaultReportingQueryParams(
    currentUser
  );
  useErrorLogger('default q', errorDefaultQ);
  const [search, setSearch] = useStringQueryParam('s', '');
  const [sort, setSort] = useTypedStringQueryParam<ReportDrilldownSortKeys>(
    'sort',
    ReportDrilldownSortKeys.COMPLETION_RATE_ASC
  );

  const isDadoEmployee = useIsCurrentUserDadoEmployee();

  const actualQ = q || defaultQ;

  // TODO check if user can view the requested tpls
  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <TeaserBoxFreeTierForExperiences>
        {t(
          'This is a preview of the reporting Dado offers for Experiences, using sample data.'
        )}
      </TeaserBoxFreeTierForExperiences>
      <PageToolbar>
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('Completion Drill-down report')}
          </Typography>
        </PageToolbarTitle>
      </PageToolbar>

      {!actualQ && loadingQ && <Loader height={500} />}
      {actualQ &&
        defaultQ &&
        (actualQ.tplIds.length ? (
          <Body
            q={actualQ}
            setQ={setQ}
            defaultQ={defaultQ}
            search={search}
            setSearch={setSearch}
            sort={sort}
            setSort={setSort}
          />
        ) : (
          <EmptyStateNoReportableTemplates />
        ))}
    </PageBody>
  );
};
